import React from 'react';
import LocalIcon from 'ui/LocalIcon';
import Icon from 'components/Icon';
import {
  GeminiIconWrapper,
  GenerateContentViaAIButton,
  GenerateContentViaAiLabel,
  GeneratingDots,
} from '../TitleInput/styles';
import {
  AI_GPT_MODEL_OPTIONS,
  formatAiCompletionDuration,
} from 'api/chatbot-api/aiModels';
import {
  AiCompletionTimer,
  AiCompletionTimerValue,
  AiModelField,
  AiModelLabel,
  AiModelRadioGroup,
  AutoCompleteAiControls,
  AutoCompleteAiPrimaryRow,
} from './styles';
import { useTranslation } from 'react-i18next';
import { isANumber } from 'utils/number';
import Input from 'components/Input/Input';

interface IAutoCompleteAiBtnProps {
  generating: boolean;
  onClick?: () => void;
  disabled?: boolean;
  model: string;
  onModelChange: (model: string) => void;
  occuringTime?: number | null;
  completionDurationMs?: number | null;
  showGenerateButton?: boolean;
  inputName?: string;
}

const AutoCompleteAiBtn: React.FC<IAutoCompleteAiBtnProps> = ({
  generating,
  onClick,
  disabled,
  model,
  onModelChange,
  occuringTime,
  completionDurationMs,
  showGenerateButton = true,
  inputName = 'aiGptModel',
}) => {
  const { t } = useTranslation('survey');
  const showLiveTimer = generating && occuringTime !== null;
  const showFinalTimer = !generating && completionDurationMs !== null;
  const timerValue = showLiveTimer
    ? occuringTime
    : showFinalTimer
      ? completionDurationMs
      : null;

  return (
    <AutoCompleteAiControls>
      <AiModelField>
        <AiModelLabel>{t('aiGptModel') as string}</AiModelLabel>
        <AiModelRadioGroup>
          {AI_GPT_MODEL_OPTIONS.map((option) => (
            <Input
              key={option.value}
              id={`${inputName}-${option.value}`}
              type="radio"
              name={inputName}
              checked={model === option.value}
              label={option.label}
              disabled={generating || disabled}
              onChange={() => onModelChange(option.value)}
            />
          ))}
        </AiModelRadioGroup>
      </AiModelField>

      <AutoCompleteAiPrimaryRow>
        {isANumber(timerValue) ? (
          <AiCompletionTimer active={generating}>
            <Icon type="clock" style={{ width: '1rem', height: '1rem' }} />
            <span>{t('aiCompletionTime') as string}</span>
            <AiCompletionTimerValue>
              {formatAiCompletionDuration(timerValue as number)}
            </AiCompletionTimerValue>
          </AiCompletionTimer>
        ) : (
          <div></div>
        )}
        {showGenerateButton && onClick && (
          <GenerateContentViaAIButton
            generating={generating}
            disabled={generating || disabled}
            onClick={onClick}
            type="button"
          >
            <GeminiIconWrapper generating={generating}>
              <LocalIcon
                iconName="gemini-logo.svg"
                type="xs"
                color="no-color"
                style={{ margin: 0, height: '2rem' }}
              />
            </GeminiIconWrapper>
            <GenerateContentViaAiLabel>
              {generating
                ? (t('generatingContentViaAI') as string)
                : (t('generateContentViaAI') as string)}
            </GenerateContentViaAiLabel>
            {generating && (
              <GeneratingDots aria-hidden="true">
                <span />
                <span />
                <span />
              </GeneratingDots>
            )}
          </GenerateContentViaAIButton>
        )}
      </AutoCompleteAiPrimaryRow>
    </AutoCompleteAiControls>
  );
};

export default AutoCompleteAiBtn;
